import { useEffect, useState, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, RefreshCw, Loader2, Inbox, ArrowLeft, AlertCircle, Download } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLES = {
  new: "bg-accent text-accent-foreground",
  contacted: "bg-primary text-primary-foreground",
  closed: "bg-muted text-muted-foreground line-through",
};
const STATUS_LABELS = { new: "New", contacted: "Contacted", closed: "Closed" };

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.5 29.3 4.5 24 4.5 13.2 4.5 4.5 13.2 4.5 24S13.2 43.5 24 43.5 43.5 34.8 43.5 24c0-1.2-.1-2.3-.4-3.5z"/>
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 18.9 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.5 29.3 4.5 24 4.5 16.3 4.5 9.7 8.9 6.3 14.7z"/>
      <path fill="#4CAF50" d="M24 43.5c5.2 0 9.9-2 13.4-5.2l-6.2-5.2c-2 1.5-4.6 2.4-7.2 2.4-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.6 39 16.2 43.5 24 43.5z"/>
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.2 4.2-4.1 5.6l6.2 5.2c-.4.4 6.6-4.8 6.6-14.8 0-1.2-.1-2.3-.4-3.5z"/>
    </svg>
  );
}

export default function AdminDashboard() {
  const [status, setStatus] = useState("checking"); // checking | authed | unauthed
  const [user, setUser] = useState(null);
  const [requests, setRequests] = useState([]);
  const [loadingData, setLoadingData] = useState(false);
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const authError = params.get("error");

  const fetchRequests = useCallback(() => {
    setLoadingData(true);
    fetch(`${API}/service-requests`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then(setRequests)
      .catch(() => {})
      .finally(() => setLoadingData(false));
  }, []);

  useEffect(() => {
    fetch(`${API}/auth/me`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((u) => {
        setUser(u);
        setStatus("authed");
        fetchRequests();
      })
      .catch(() => setStatus("unauthed"));
  }, [fetchRequests]);

  const login = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/admin";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    await fetch(`${API}/auth/logout`, { method: "POST", credentials: "include" });
    setUser(null);
    setRequests([]);
    setStatus("unauthed");
  };

  const updateStatus = async (id, newStatus) => {
    const prev = requests;
    setRequests((rs) => rs.map((r) => (r.id === id ? { ...r, status: newStatus } : r)));
    try {
      const resp = await fetch(`${API}/service-requests/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ status: newStatus }),
      });
      if (!resp.ok) throw new Error();
      toast.success(`Marked as ${STATUS_LABELS[newStatus].toLowerCase()}`);
    } catch {
      setRequests(prev);
      toast.error("Couldn't update status. Please try again.");
    }
  };

  const exportCsv = () => {
    if (requests.length === 0) {
      toast.error("No requests to export yet.");
      return;
    }
    const cols = ["full_name", "email", "organization", "phone", "package", "status", "message", "created_at"];
    const headers = ["Name", "Email", "Organization", "Phone", "Package", "Status", "Message", "Received"];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const rows = requests.map((r) =>
      cols.map((c) => esc(c === "created_at" ? new Date(r[c]).toLocaleString() : r[c])).join(",")
    );
    const csv = [headers.map(esc).join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `align-requests-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${requests.length} request${requests.length === 1 ? "" : "s"}`);
  };

  if (status === "checking") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  if (status === "unauthed") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-6" data-testid="admin-login">
        <div className="w-full max-w-md text-center">
          <div className="flex items-center justify-center gap-2">
            <span className="h-2.5 w-2.5 rounded-full bg-primary" />
            <span className="font-serif text-2xl tracking-tight text-foreground">Align Enrollment</span>
          </div>
          <h1 className="mt-8 font-serif font-light text-4xl tracking-tight text-foreground">Staff Sign-In</h1>
          <p className="mt-3 text-sm text-muted-foreground">Sign in with your authorized Google account to view submitted requests.</p>

          {authError && (
            <div className="mt-6 flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3 text-left" data-testid="admin-auth-error">
              <AlertCircle size={16} className="mt-0.5 shrink-0 text-destructive" />
              <span className="text-sm text-destructive">{authError}</span>
            </div>
          )}

          <button
            data-testid="google-signin-button"
            onClick={login}
            className="mt-8 inline-flex w-full items-center justify-center gap-3 rounded-full border border-border bg-card px-6 py-3.5 text-sm font-medium text-foreground transition-transform duration-200 hover:-translate-y-0.5"
          >
            <GoogleIcon /> Sign in with Google
          </button>

          <button
            data-testid="back-to-site"
            onClick={() => navigate("/")}
            className="mt-6 inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.15em] text-muted-foreground hover:text-foreground transition-colors duration-200"
          >
            <ArrowLeft size={14} /> Back to site
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="admin-dashboard">
      <header className="border-b border-border bg-card">
        <div className="mx-auto max-w-[1440px] px-6 md:px-12 py-5 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="h-2.5 w-2.5 rounded-full bg-primary" />
            <span className="font-serif text-xl tracking-tight text-foreground">Align Enrollment — Admin</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="hidden sm:block text-sm text-muted-foreground" data-testid="admin-user-email">{user?.email}</span>
            <button data-testid="admin-logout" onClick={logout} className="inline-flex items-center gap-2 rounded-full border border-border px-4 py-2 text-sm text-foreground transition-colors duration-200 hover:bg-secondary">
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1440px] px-6 md:px-12 py-12">
        <div className="flex items-end justify-between mb-8">
          <div>
            <span className="font-mono text-xs uppercase tracking-[0.25em] text-muted-foreground">Submitted requests</span>
            <h1 className="mt-3 font-serif font-light text-4xl tracking-tight text-foreground" data-testid="requests-count">
              {requests.length} {requests.length === 1 ? "request" : "requests"}
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <button data-testid="export-csv" onClick={exportCsv} className="inline-flex items-center gap-2 rounded-full border border-border px-5 py-2.5 text-sm text-foreground transition-colors duration-200 hover:bg-secondary">
              <Download size={14} /> Export CSV
            </button>
            <button data-testid="refresh-requests" onClick={fetchRequests} className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm text-primary-foreground transition-transform duration-200 hover:-translate-y-0.5">
              <RefreshCw size={14} className={loadingData ? "animate-spin" : ""} /> Refresh
            </button>
          </div>
        </div>

        {requests.length === 0 ? (
          <div className="border border-dashed border-border rounded-lg py-24 flex flex-col items-center justify-center text-center" data-testid="empty-state">
            <Inbox size={32} strokeWidth={1.3} className="text-muted-foreground" />
            <p className="mt-4 text-sm text-muted-foreground">No requests yet. New submissions will appear here.</p>
          </div>
        ) : (
          <div className="overflow-x-auto border border-border rounded-lg">
            <table className="w-full text-left text-sm">
              <thead className="bg-secondary">
                <tr className="text-muted-foreground font-mono text-xs uppercase tracking-[0.1em]">
                  <th className="px-5 py-4 font-medium">Name</th>
                  <th className="px-5 py-4 font-medium">Package</th>
                  <th className="px-5 py-4 font-medium">Status</th>
                  <th className="px-5 py-4 font-medium">Email</th>
                  <th className="px-5 py-4 font-medium">Organization</th>
                  <th className="px-5 py-4 font-medium">Phone</th>
                  <th className="px-5 py-4 font-medium">Message</th>
                  <th className="px-5 py-4 font-medium">Received</th>
                </tr>
              </thead>
              <tbody>
                {requests.map((r, i) => (
                  <tr key={r.id} className="border-t border-border align-top" data-testid={`request-row-${i}`}>
                    <td className="px-5 py-4 font-medium text-foreground whitespace-nowrap">{r.full_name}</td>
                    <td className="px-5 py-4"><span className="inline-block rounded-full bg-accent px-3 py-1 text-xs text-accent-foreground">{r.package}</span></td>
                    <td className="px-5 py-4">
                      <div className="flex flex-col gap-2">
                        <span data-testid={`request-status-${i}`} className={`inline-block w-fit rounded-full px-3 py-1 text-xs ${STATUS_STYLES[r.status] || STATUS_STYLES.new}`}>
                          {STATUS_LABELS[r.status] || "New"}
                        </span>
                        <div className="flex gap-1.5">
                          {["contacted", "closed", "new"]
                            .filter((s) => s !== (r.status || "new"))
                            .map((s) => (
                              <button
                                key={s}
                                data-testid={`set-${s}-${i}`}
                                onClick={() => updateStatus(r.id, s)}
                                className="rounded-full border border-border px-2.5 py-1 text-[11px] text-muted-foreground transition-colors duration-200 hover:bg-secondary hover:text-foreground whitespace-nowrap"
                              >
                                Mark {STATUS_LABELS[s].toLowerCase()}
                              </button>
                            ))}
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-4 text-muted-foreground whitespace-nowrap">
                      <a href={`mailto:${r.email}`} className="hover:text-primary transition-colors duration-200">{r.email}</a>
                    </td>
                    <td className="px-5 py-4 text-muted-foreground">{r.organization || "—"}</td>
                    <td className="px-5 py-4 text-muted-foreground whitespace-nowrap">{r.phone || "—"}</td>
                    <td className="px-5 py-4 text-muted-foreground max-w-xs">{r.message || "—"}</td>
                    <td className="px-5 py-4 text-muted-foreground whitespace-nowrap">
                      {new Date(r.created_at).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
}
